import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import styles from "./WhatVertikIs.module.scss";

const tabs = [
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week-ahead", label: "Week ahead" },
  { id: "coach", label: "Coach" },
];

const tabContent = {
  today: {
    label: "Command Center",
    headline: "Wake up to a sales day that already makes sense",
    description:
      "Vertik gives every seller a live command center for Today, Tomorrow, Week Ahead, and Coach — turning meetings, follow-ups, hot leads, deal risks, and overdue tasks into one clear plan.",
    features: [
      "See today's highest-impact actions first",
      "Prepare tomorrow before it becomes chaotic",
      "Spot week-ahead risks before deals slip",
      "Get coaching based on how you actually sell",
    ],
  },
  tomorrow: {
    label: "Tomorrow View",
    headline: "Start tomorrow with clarity, not a blank calendar",
    description:
      "Vertik lines up tomorrow's meetings, follow-ups, and pipeline moves before you log off today — so you never start the next day rebuilding context.",
    features: [
      "Preview tomorrow's meetings with prep briefs ready",
      "Queue follow-ups ranked by deal impact",
      "Block time for high-priority outreach",
      "See what moved overnight across your pipeline",
    ],
  },
  "week-ahead": {
    label: "Week Ahead",
    headline: "See risks and opportunities before the week runs you",
    description:
      "Vertik scans your pipeline, accounts, and calendar for the week ahead — surfacing deals at risk, quiet accounts, and the moves that matter most.",
    features: [
      "Flag deals likely to slip this week",
      "Spot accounts going quiet before renewal risk",
      "Map stakeholder gaps across active opportunities",
      "Balance your week by revenue impact, not urgency alone",
    ],
  },
  coach: {
    label: "Coach",
    headline: "Coaching that happens inside the work",
    description:
      "Vertik learns how you sell and coaches in the moment — on follow-up discipline, meeting prep, objection handling, and response time — not at the next pipeline review.",
    features: [
      "Get nudges when follow-up patterns need attention",
      "Improve meeting prep with account-specific guidance",
      "Sharpen objection handling from real deal context",
      "Build better habits without extra manager overhead",
    ],
  },
};

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.5L6.5 11.5L12.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WhatVertikIs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [activeTab, setActiveTab] = useState("today");

  const content = tabContent[activeTab];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="what-vertik-is"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <motion.h2 className={styles.sectionHeadline} variants={itemVariants}>
          Everything a seller needs to move faster — working together
        </motion.h2>

        <motion.div className={styles.panel} variants={itemVariants} data-section-panel="true">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className={styles.content}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <span className={styles.contentLabel}>{content.label}</span>
              <h3 className={styles.contentHeadline}>{content.headline}</h3>
              <p className={styles.contentDescription}>{content.description}</p>

              <ul className={styles.featureList}>
                {content.features.map((feature) => (
                  <li key={feature} className={styles.featureItem}>
                    <span className={styles.checkIcon}>
                      <CheckIcon />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>

          <nav className={styles.tabNav} aria-label="What Vertik is views">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                  aria-selected={isActive}
                  role="tab"
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default WhatVertikIs;
